import { HttpService } from "../../modules/shared/services/http.service";
import { 
  CommonQuestion, 
  CommonQuestionCreateDto, 
  CommonQuestionUpdateDto,
  CommonQuestionFilters,
  CommonQuestionResponse,
  CommonQuestionSort
} from "../../modules/shared/model/common-questions.interfaces";

const headers = { "Content-Type": "application/json" } as const;

// Create Common Question
export const createCommonQuestion = async (data: CommonQuestionCreateDto): Promise<CommonQuestion> => {
  const response = await HttpService.post("api/v1/common-questions", data, headers, true);
  return response;
};

// Update Common Question
export const updateCommonQuestion = async (id: string, data: CommonQuestionUpdateDto): Promise<CommonQuestion> => {
  const response = await HttpService.patch(`api/v1/common-questions/${id}`, data, headers, true);
  return response;
};

// Get Common Question by ID
export const getCommonQuestionById = async (id: string): Promise<CommonQuestion> => {
  const response = await HttpService.get(`api/v1/common-questions/${id}`, true, headers);
  return response;
};

// Get All Common Questions
export const getCommonQuestions = async (filters?: CommonQuestionFilters): Promise<CommonQuestionResponse> => {
  const {
    page = 1,
    limit = 10,
    search = "",
    sortBy = "createdAt",
    sortOrder = "desc",
    category,
    language,
    country,
    isShowLoader = true
  } = filters || {};

  let endpoint = `api/v1/common-questions?page=${page}&limit=${limit}`;
  
  // Add sorting
  const sort: CommonQuestionSort[] = [{ orderBy: sortBy, order: sortOrder }];
  endpoint += `&sort=${encodeURIComponent(JSON.stringify(sort))}`;
  
  // Combine all filters into a single object
  const filterObject: any = {};
  if (search) filterObject.search = search;
  if (category) filterObject.category = category;
  if (language) filterObject.language = language;
  if (country) filterObject.countries = [country]; // Backend expects countries array
  
  // Add filters if any exist
  if (Object.keys(filterObject).length > 0) {
    endpoint += `&filters=${encodeURIComponent(JSON.stringify(filterObject))}`;
  }

  const response = await HttpService.get(endpoint, isShowLoader, headers);
  return response;
};

// Get Common Questions by Country
export const getCommonQuestionsByCountry = async (countryId: string, filters?: CommonQuestionFilters): Promise<CommonQuestionResponse> => {
  const {
    page = 1,
    limit = 10,
    search = "",
    sortBy = "createdAt",
    sortOrder = "desc",
    category,
    language,
    isShowLoader = true
  } = filters || {};

  let endpoint = `api/v1/common-questions/by-country/${countryId}?page=${page}&limit=${limit}`;
  
  // Add sorting
  const sort: CommonQuestionSort[] = [{ orderBy: sortBy, order: sortOrder }];
  endpoint += `&sort=${encodeURIComponent(JSON.stringify(sort))}`;
  
  // Add filters
  if (search) {
    endpoint += `&filters=${encodeURIComponent(JSON.stringify({ search }))}`;
  }
  if (category) {
    endpoint += `&filters=${encodeURIComponent(JSON.stringify({ category }))}`;
  }
  if (language) {
    endpoint += `&filters=${encodeURIComponent(JSON.stringify({ language }))}`;
  }

  const response = await HttpService.get(endpoint, isShowLoader, headers);
  return response;
};

// Delete Common Question
export const deleteCommonQuestion = async (id: string): Promise<void> => {
  await HttpService.deleteRecord(`api/v1/common-questions/${id}`, headers, true);
};

// Bulk Create Common Questions
export const bulkCreateCommonQuestions = async (data: { commonQuestions: CommonQuestionCreateDto[] }): Promise<CommonQuestion[]> => {
  const response = await HttpService.post("api/v1/common-questions/bulk", data, headers, true);
  return response;
};

// Bulk Update Common Questions
export const bulkUpdateCommonQuestions = async (data: { commonQuestions: Array<{ id: string; data: CommonQuestionUpdateDto }> }): Promise<CommonQuestion[]> => {
  const response = await HttpService.patch("api/v1/common-questions/bulk", data, headers, true);
  return response;
};
